import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Cell, BarChart,
} from "recharts";
import {
  AlertTriangle, ChevronRight, HelpCircle, ChevronDown, TrendingDown, TrendingUp, Edit3, Download, Table2, BarChart3, AlertCircle,
} from "lucide-react";

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const fmtE = (v) => {
  if (v == null) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(2)} GWh`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)} MWh`;
  return `${n.toFixed(0)} kWh`;
};
const tick = (ts = "") => {
  if (ts.length === 7) return `${MON[+ts.slice(5, 7) - 1]} '${ts.slice(2, 4)}`;
  if (ts.length === 10) return `${+ts.slice(8, 10)} ${MON[+ts.slice(5, 7) - 1]}`;
  return ts.slice(11, 16) || ts;
};
const longDate = (ts = "") => {
  if (!ts) return "";
  const y = ts.slice(0, 4), m = MON[+ts.slice(5, 7) - 1];
  return ts.length >= 10 ? `${ts.slice(8, 10)}/${ts.slice(5, 7)}/${y}` : `${m} ${y}`;
};

function Head({ title, desc, right, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap mb-5" data-testid={testid}>
      <div>
        <h2 className="ork-h2">{title} <HelpCircle size={16} className="ork-help" /></h2>
        {desc && <p className="ork-desc">{desc}</p>}
      </div>
      {right}
    </div>
  );
}

function VarBar({ pct }) {
  if (pct == null) return <span className="ork-var-track" />;
  const w = Math.min(16, Math.max(3, Math.abs(pct) / 2));
  const pos = pct >= 0;
  return (
    <span className="ork-var-track">
      <span className="ork-var-bar" style={{ width: w, left: pos ? 17 : 17 - w, background: pos ? "var(--ok-pos)" : "var(--ok-neg)" }} />
    </span>
  );
}

export function KpiStrip({ s, reporting, total, fourth, actualSub, prefix = "pk" }) {
  const pct = s.variance_pct;
  const pos = (s.variance_kwh || 0) >= 0;
  return (
    <section className="ork-panel grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8" data-testid={prefix === "pk" ? "portfolio-kpis" : "site-kpis"}>
      <div data-testid={`${prefix}-actual`}>
        <div className="ork-kpi-label">Actual generation</div>
        <div className="ork-kpi-value">{fmtE(s.actual_kwh)}</div>
        <div className="ork-kpi-sub">{actualSub || `Metered energy produced across the portfolio · ${s.sample_count.toLocaleString()} samples.`}</div>
      </div>
      <div data-testid={`${prefix}-estimate`}>
        <div className="ork-kpi-label">Generation potential</div>
        <div className="ork-kpi-value">{fmtE(s.estimated_kwh)}</div>
        <div className="ork-kpi-sub">Expected output from local irradiance (weather model) for the period.</div>
      </div>
      <div data-testid={`${prefix}-variance`}>
        <div className="ork-kpi-label">Variation from potential</div>
        {pct != null && s.estimated_kwh ? (
          <div className="flex items-center gap-4 mt-1">
            <span className="inline-flex items-center gap-2 text-lg font-bold"><VarBar pct={pct} />{Math.abs(pct).toFixed(1)}%</span>
            <span className={`ork-kpi-value !mt-0 ${pos ? "ork-pos" : "ork-neg"}`}>{`${pos ? "+" : "−"}${fmtE(Math.abs(s.variance_kwh))}`}</span>
          </div>
        ) : <div className="ork-kpi-value">—</div>}
        <div className="ork-kpi-sub">{s.estimated_kwh ? "Actual vs potential generation for the period." : "No weather potential yet for this window."}</div>
      </div>
      {fourth || (
        <div data-testid="pk-sites">
          <div className="ork-kpi-label">Sites reporting</div>
          <div className="ork-kpi-value">{reporting}<span className="text-xl font-semibold" style={{ color: "var(--ink-3)" }}> / {total}</span></div>
          <div className="ork-kpi-sub">Sites with metered data in the selected window.</div>
        </div>
      )}
    </section>
  );
}

const TipBox = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-white px-3 py-2 text-xs shadow-xl" style={{ border: "1px solid var(--line)" }}>
      <div className="font-bold mb-1">{longDate(label)}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || p.stroke }}>{p.name}: <b>{unit === "%" ? `${Number(p.value).toFixed(1)}%` : fmtE(p.value)}</b></div>
      ))}
    </div>
  );
};

function LegendItem({ kind, label }) {
  const icon = kind === "bar"
    ? <span className="w-3.5 h-3.5 rounded-full" style={{ background: "linear-gradient(180deg,#00D6DF,#00B3BB)" }} />
    : <span className="inline-flex items-center gap-[3px]"><span className="w-1.5 h-[2px] bg-[var(--warm)]" /><span className="w-2 h-2 rounded-full bg-[var(--warm)]" /><span className="w-1.5 h-[2px] bg-[var(--warm)]" /></span>;
  return <span className="inline-flex items-center gap-2 text-sm" style={{ color: "var(--ink-2)" }}>{icon}{label}</span>;
}

export function GenerationChart({ series, testid = "portfolio-chart" }) {
  return (
    <section className="ork-panel mb-8" data-testid={testid}>
      <Head title="Generation vs potential"
        desc={`Actual generation compared to potential generation based on local weather data${series.length ? ` (${longDate(series[0].ts)} – ${longDate(series[series.length - 1].ts)})` : ""}.`} />
      {series.length ? (
        <>
          <div style={{ width: "100%", height: 330 }}>
            <ResponsiveContainer width="99%" height="100%">
              <ComposedChart data={series.map((r) => ({ ...r, estimated_kwh: r.estimated_kwh || null }))} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}>
                <defs>
                  <linearGradient id="orkBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00D6DF" stopOpacity={0.95} /><stop offset="100%" stopColor="#00D6DF" stopOpacity={0.45} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E1E7E8" vertical={false} />
                <XAxis dataKey="ts" tickFormatter={tick} tick={{ fontSize: 10, fill: "#7C8B8C", fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#E1E7E8" }} tickLine={false} minTickGap={18} />
                <YAxis tickFormatter={fmtE} tick={{ fontSize: 10, fill: "#7C8B8C", fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#E1E7E8" }} tickLine={false} width={86} />
                <Tooltip content={<TipBox />} cursor={{ fill: "#F4F8F8" }} />
                <Bar dataKey="actual_kwh" name="Solar generation" fill="url(#orkBar)" radius={[6, 6, 0, 0]} maxBarSize={46} />
                <Line dataKey="estimated_kwh" name="Generation potential" stroke="#B33C86" strokeWidth={2} strokeDasharray="3 4"
                  dot={{ r: 3, fill: "#B33C86", strokeWidth: 0 }} activeDot={{ r: 5 }} type="monotone" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-center gap-8 mt-3 flex-wrap">
            <LegendItem kind="bar" label="Solar generation" />
            <LegendItem kind="line" label="Generation potential" />
          </div>
        </>
      ) : (
        <div className="text-sm py-10 text-center flex items-center justify-center gap-2" style={{ color: "var(--ink-2)" }}>
          <AlertCircle size={15} /> No production data yet — add a site with meter credentials and hit Sync all.
        </div>
      )}
    </section>
  );
}

export function VariationChart({ series, testid = "portfolio-variation-chart" }) {
  const [unit, setUnit] = useState("%");
  const data = series.map((r) => ({
    ts: r.ts,
    v: unit === "%" ? r.variance_pct : (r.estimated_kwh ? (r.actual_kwh || 0) - r.estimated_kwh : null),
  }));
  const has = data.some((d) => d.v != null);
  return (
    <section className="ork-panel mb-8" data-testid={testid}>
      <Head title="Variation from potential" desc="Variation of actual generation from the weather-based potential." />
      <div className="ork-seg mb-5" data-testid="variation-unit-toggle">
        {[["kWh", "kWh"], ["%", "%"]].map(([k, l]) => (
          <button key={k} onClick={() => setUnit(k)} className={unit === k ? "is-on" : ""} data-testid={`variation-unit-${k === "%" ? "pct" : "kwh"}`}>{l}</button>
        ))}
      </div>
      {has ? (
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer width="99%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 4 }}>
              <CartesianGrid stroke="#E1E7E8" vertical={false} />
              <XAxis dataKey="ts" tickFormatter={tick} tick={{ fontSize: 10, fill: "#7C8B8C", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} minTickGap={18} />
              <YAxis tickFormatter={(v) => (unit === "%" ? `${v > 0 ? "+" : ""}${v}%` : fmtE(v))} tick={{ fontSize: 10, fill: "#7C8B8C", fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#E1E7E8" }} tickLine={false} width={86} />
              <Tooltip content={<TipBox unit={unit} />} cursor={{ fill: "#F4F8F8" }} />
              <ReferenceLine y={0} stroke="#7C8B8C" strokeDasharray="4 4" />
              <Bar dataKey="v" name="Variation" radius={[5, 5, 5, 5]} maxBarSize={46}>
                {data.map((d, i) => <Cell key={i} fill={(d.v || 0) >= 0 ? "#10B981" : "#C9851A"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-sm py-8 text-center" style={{ color: "var(--ink-2)" }} data-testid="variation-empty">
          No weather potential for this window yet — run Backfill weather to compare.
        </div>
      )}
    </section>
  );
}

const SORTS = [
  { key: "pct_desc", label: "Best to worst (%)", Icon: TrendingDown, f: (r) => r.variance_pct, dir: -1 },
  { key: "pct_asc", label: "Worst to best (%)", Icon: TrendingUp, f: (r) => r.variance_pct, dir: 1 },
  { key: "kwh_desc", label: "Best to worst (kWh)", Icon: TrendingDown, f: (r) => r.variance_kwh, dir: -1 },
  { key: "kwh_asc", label: "Worst to best (kWh)", Icon: TrendingUp, f: (r) => r.variance_kwh, dir: 1 },
];
const COL_SORTS = { name: (r) => (r.name || r.customer_name || "").toLowerCase(), actual: (r) => r.actual_kwh, est: (r) => r.estimated_kwh };

function SortTh({ id, label, sort, setSort, num }) {
  const on = sort.key === id;
  return (
    <th className={num ? "num" : ""}>
      <button className={`ork-sort ${on ? "is-on" : ""}`} onClick={() => setSort({ key: id, dir: on ? -sort.dir : (id === "name" ? 1 : -1) })} data-testid={`site-sort-${id}`}>
        {label} <ChevronDown size={15} style={{ transform: on && sort.dir > 0 ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>
    </th>
  );
}

function SiteTable({ sites, onSelectSite, onEdit }) {
  const [sort, setSort] = useState({ key: "pct_asc", dir: 1 });
  const [menu, setMenu] = useState(false);
  const [per, setPer] = useState(10);
  const [page, setPage] = useState(1);
  const sorted = useMemo(() => {
    const preset = SORTS.find((x) => x.key === sort.key);
    const f = preset ? preset.f : COL_SORTS[sort.key];
    const dir = preset ? preset.dir : sort.dir;
    return [...sites].sort((a, b) => {
      const x = f(a), y = f(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return x < y ? -dir : x > y ? dir : 0;
    });
  }, [sites, sort]);
  const pages = Math.max(1, Math.ceil(sorted.length / per));
  const cur = Math.min(page, pages);
  const rows = sorted.slice((cur - 1) * per, cur * per);
  const presetOn = SORTS.some((x) => x.key === sort.key);

  return (
    <section className="ork-panel !px-0 !pb-3" data-testid="portfolio-site-table">
      <div className="px-7">
        <Head title="Site performance" desc="Per-site generation against weather potential for the selected period."
          right={
            <div className="flex items-center gap-5">
              <label className="text-sm inline-flex items-center gap-1" style={{ color: "var(--ink-2)" }}>
                <select value={per} onChange={(e) => { setPer(+e.target.value); setPage(1); }} className="bg-transparent outline-none cursor-pointer" data-testid="site-table-per-page">
                  {[10, 20, 50].map((n) => <option key={n} value={n}>{n} per page</option>)}
                </select>
              </label>
              <div className="ork-pager flex gap-1" data-testid="site-table-pager">
                {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                  <button key={n} onClick={() => setPage(n)} className={n === cur ? "is-on" : ""} data-testid={`site-table-page-${n}`}>{n}</button>
                ))}
              </div>
            </div>
          } />
      </div>
      <div className="overflow-x-auto">
        <table className="ork-table">
          <thead>
            <tr>
              <SortTh id="name" label="Site" sort={sort} setSort={setSort} />
              <SortTh id="actual" label="Generation" sort={sort} setSort={setSort} num />
              <SortTh id="est" label="Potential" sort={sort} setSort={setSort} num />
              <th className="num relative">
                <button className={`ork-sort ${presetOn ? "is-on" : ""}`} onClick={() => setMenu((m) => !m)} data-testid="site-sort-variance">
                  <TrendingUp size={15} /> Generation vs potential <ChevronDown size={15} />
                </button>
                {menu && (
                  <div className="ork-menu" data-testid="site-sort-menu">
                    {SORTS.map(({ key, label, Icon }) => (
                      <button key={key} className={sort.key === key ? "is-on" : ""} onClick={() => { setSort({ key, dir: 1 }); setMenu(false); }} data-testid={`site-sort-${key}`}>
                        <Icon size={16} /> {label}
                      </button>
                    ))}
                  </div>
                )}
              </th>
              <th>Source</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const pos = (p.variance_kwh || 0) >= 0;
              return (
                <tr key={p.id} onClick={() => onSelectSite(p.id)} data-testid={`portfolio-site-row-${p.id}`}>
                  <td className="font-semibold">{p.name || p.customer_name}</td>
                  <td className="num">{fmtE(p.actual_kwh)}</td>
                  <td className="num" style={{ color: "var(--ink-2)" }}>{fmtE(p.estimated_kwh)}</td>
                  <td className="num">
                    <span className="ork-var" data-testid={`portfolio-site-variance-${p.id}`}>
                      <VarBar pct={p.variance_pct} />
                      <span className="text-xs" style={{ color: "var(--ink-2)", minWidth: 42 }}>{p.variance_pct != null ? `${p.variance_pct > 0 ? "+" : ""}${p.variance_pct.toFixed(0)}%` : "—"}</span>
                      <span className={`font-semibold text-base ${p.variance_pct != null ? (pos ? "ork-pos" : "ork-neg") : ""}`} style={{ minWidth: 92 }}>
                        {p.variance_pct != null ? `${pos ? "+" : "−"}${fmtE(Math.abs(p.variance_kwh))}` : "—"}
                      </span>
                    </span>
                  </td>
                  <td style={{ color: "var(--ink-2)" }}>{p.solaredge_configured ? "SolarEdge" : (p.actual_kwh ? "Meter" : "Not connected")}</td>
                  <td className="num" onClick={(e) => e.stopPropagation()}>
                    {onEdit && (
                      <button onClick={() => onEdit(p)} className="p-1.5 rounded-full hover:bg-[var(--surface)]" data-testid={`portfolio-site-edit-${p.id}`} title="Edit site">
                        <Edit3 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!sites.length && <tr><td colSpan={6} className="text-center py-8" style={{ color: "var(--ink-2)" }}>No sites yet. Click Add site to get started.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SeriesTable({ series }) {
  return (
    <section className="ork-panel !px-0 mb-8" data-testid="portfolio-series-table">
      <div className="px-7"><Head title="Generation by period" desc="The same data as the charts, period by period." /></div>
      <div className="overflow-x-auto max-h-[520px]">
        <table className="ork-table">
          <thead><tr><th>Period</th><th className="num">Generation</th><th className="num">Potential</th><th className="num">Variation</th></tr></thead>
          <tbody>
            {series.map((r) => (
              <tr key={r.ts} style={{ cursor: "default" }}>
                <td className="font-semibold">{longDate(r.ts)}{r.ts.length > 10 ? ` ${r.ts.slice(11, 16)}` : ""}</td>
                <td className="num">{fmtE(r.actual_kwh)}</td>
                <td className="num" style={{ color: "var(--ink-2)" }}>{fmtE(r.estimated_kwh)}</td>
                <td className={`num font-semibold ${r.variance_pct == null ? "" : r.variance_pct >= 0 ? "ork-pos" : "ork-neg"}`}>
                  {r.variance_pct != null ? `${r.variance_pct > 0 ? "+" : ""}${r.variance_pct.toFixed(1)}%` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const gbp = (v) => (v == null ? "—" : `£${Math.round(Math.abs(v)).toLocaleString("en-GB")}`);

function ShortfallAlerts({ items, threshold, perSite, onSelectSite }) {
  const [open, setOpen] = useState(null);
  if (!items.length) return null;
  const total = items.reduce((a, x) => a + x.shortfall_gbp, 0);
  return (
    <div className="mt-5 rounded-[var(--radius-sm)] border" style={{ borderColor: "rgb(201 133 26 / .45)", background: "rgb(201 133 26 / .08)" }} data-testid="shortfall-alerts">
      <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
        <AlertTriangle size={17} style={{ color: "var(--warn)" }} />
        <div className="font-semibold text-sm" data-testid="shortfall-headline">
          {items.length} month{items.length === 1 ? "" : "s"} with recorded income more than {threshold}% below forecast
        </div>
        <span className="font-mono text-xs ml-auto" style={{ color: "var(--warn)" }} data-testid="shortfall-total">−{gbp(total)} vs forecast</span>
      </div>
      <div className="border-t" style={{ borderColor: "rgb(201 133 26 / .3)" }}>
        {items.map((m) => {
          const isOpen = open === m.month;
          return (
            <div key={m.month} data-testid={`shortfall-month-${m.month}`}>
              <button onClick={() => setOpen(isOpen ? null : m.month)} className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-[rgb(201_133_26_/_0.08)] transition-colors" data-testid={`shortfall-toggle-${m.month}`}>
                {perSite ? <span className="w-[14px]" /> : <ChevronRight size={14} style={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }} />}
                <span className="font-semibold w-24">{longDate(m.month)}</span>
                <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>{gbp(m.recorded_gbp)} of {gbp(m.forecast_gbp)}</span>
                <span className="font-mono text-xs font-bold ml-auto" style={{ color: "var(--warn)" }}>−{m.shortfall_pct}% · −{gbp(m.shortfall_gbp)}</span>
                {!perSite && <span className="text-xs" style={{ color: "var(--ink-3)" }}>{m.sites.length} site{m.sites.length === 1 ? "" : "s"}</span>}
              </button>
              {isOpen && !perSite && (
                <div className="px-4 pb-3 pl-11" data-testid={`shortfall-sites-${m.month}`}>
                  {m.sites.length === 0 && <div className="text-xs" style={{ color: "var(--ink-3)" }}>No single site is below threshold — the gap is spread across the portfolio.</div>}
                  {m.sites.map((x) => (
                    <button key={x.project_id} onClick={() => onSelectSite && onSelectSite(x.project_id)}
                      className="w-full flex items-center gap-3 py-1.5 text-left text-sm group" data-testid={`shortfall-site-${m.month}-${x.project_id}`}>
                      <span className="font-medium group-hover:underline truncate">{x.project_name}</span>
                      {x.no_income_recorded && <span className="tag !text-[9px]" data-testid="shortfall-no-income">No income recorded</span>}
                      <span className="font-mono text-xs ml-auto" style={{ color: "var(--ink-2)" }}>{gbp(x.recorded_gbp)} / {gbp(x.forecast_gbp)}</span>
                      <span className="font-mono text-xs font-bold w-28 text-right" style={{ color: "var(--warn)" }}>−{x.shortfall_pct}% · −{gbp(x.shortfall_gbp)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const MonTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-xs shadow-xl" style={{ background: "var(--panel)", border: "1px solid var(--line)" }}>
      <div className="font-bold mb-1">{longDate(label)}</div>
      {payload.map((p, i) => <div key={i} style={{ color: "var(--ink-2)" }}>{p.name}: <b style={{ color: "var(--ink)" }}>{gbp(p.value)}</b></div>)}
    </div>
  );
};

function RevenueTrend({ monthly, short }) {
  if (!monthly.length) return null;
  return (
    <div className="mt-6" data-testid="revenue-trend-chart">
      <div className="ork-kpi-label mb-3">Month by month</div>
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer width="99%" height="100%">
          <ComposedChart data={monthly} margin={{ top: 8, right: 8, bottom: 0, left: 4 }} barGap={3}>
            <CartesianGrid stroke="#E1E7E8" vertical={false} />
            <XAxis dataKey="month" tickFormatter={tick} tick={{ fontSize: 10, fill: "#7C8B8C", fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#E1E7E8" }} tickLine={false} />
            <YAxis tickFormatter={(v) => `£${v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : Math.round(v)}`} tick={{ fontSize: 10, fill: "#7C8B8C", fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#E1E7E8" }} tickLine={false} width={56} />
            <Tooltip content={<MonTip />} cursor={{ fill: "rgb(0 214 223 / 0.06)" }} />
            <Bar dataKey="forecast_gbp" name="Forecast revenue" fill="#00D6DF" radius={[4, 4, 0, 0]} maxBarSize={26} />
            <Bar dataKey="recorded_gbp" name="Recorded income" fill="#314041" radius={[4, 4, 0, 0]} maxBarSize={26}>
              {monthly.map((m) => <Cell key={m.month} fill={short.has(m.month) ? "#C9851A" : "#314041"} />)}
            </Bar>
            <Line dataKey="metered_gbp" name="Metered revenue" stroke="#B33C86" strokeWidth={2} strokeDasharray="3 4" dot={{ r: 2.5, fill: "#B33C86", strokeWidth: 0 }} type="monotone" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center justify-center gap-6 mt-2 flex-wrap text-sm" style={{ color: "var(--ink-2)" }}>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: "#00D6DF" }} />Forecast revenue</span>
        <span className="inline-flex items-center gap-2"><svg width="12" height="12"><rect width="12" height="12" rx="2" fill="#314041" /></svg>Recorded income</span>
        <span className="inline-flex items-center gap-2"><span className="w-4 border-t-2 border-dashed" style={{ borderColor: "var(--warm)" }} />Metered revenue</span>
        {short.size > 0 && <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: "#C9851A" }} />Shortfall month</span>}
      </div>
    </div>
  );
}

export function RevenuePanel({ rangeQuery, projectId, onSelectSite }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    setD(null);
    api.get(`/analytics/revenue-forecast?${rangeQuery}${projectId ? `&project_id=${projectId}` : ""}`)
      .then(({ data }) => alive && setD(data)).catch(() => alive && setD(false));
    return () => { alive = false; };
  }, [rangeQuery, projectId]);
  const s = d?.summary;
  const pos = (s?.variance_gbp || 0) >= 0;
  const Chip = ({ label, value, testid, total }) => (
    <div className={`ork-sum-chip ${total ? "is-total" : ""}`} data-testid={testid}>
      <div className="ork-kpi-label" style={total ? { color: "rgba(255,255,255,.8)" } : {}}>{label}</div>
      <div className="font-display text-2xl font-bold mt-1">{value}</div>
    </div>
  );
  return (
    <section className="ork-panel mb-8" data-testid="revenue-forecast-panel">
      <Head title="Revenue vs forecast"
        desc={`Recorded income against forecast revenue — weather potential kWh priced at each site's PPA (self-consumption) and export tariffs${d ? ` (${longDate(d.start)} – ${longDate(d.end)})` : ""}.`} />
      {d === null ? <div className="text-sm" style={{ color: "var(--ink-3)" }}>Loading…</div> : d === false ? (
        <div className="text-sm" style={{ color: "var(--ink-3)" }} data-testid="revenue-forecast-error">Revenue forecast unavailable.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1.2fr] items-center gap-3">
            <Chip label="Forecast revenue" value={gbp(s.forecast_gbp)} testid="rf-forecast" />
            <span className="hidden md:block text-xl text-center" style={{ color: "var(--ink-3)" }}>vs</span>
            <Chip label="Recorded income" value={gbp(s.recorded_gbp)} testid="rf-recorded" />
            <span className="hidden md:block text-xl text-center" style={{ color: "var(--ink-3)" }}>=</span>
            <Chip total label="Variation from forecast" testid="rf-variance"
              value={s.variance_gbp == null ? "—" : `${pos ? "+" : "−"}${gbp(s.variance_gbp)}${s.variance_pct != null ? ` · ${s.variance_pct > 0 ? "+" : ""}${s.variance_pct}%` : ""}`} />
          </div>
          <div className="ork-kpi-sub mt-4" data-testid="rf-metered">
            Metered revenue (actual kWh × tariffs): <b style={{ color: "var(--ink)" }}>{gbp(s.metered_gbp)}</b>
            {s.forecast_gbp === 0 && " · No weather potential in this window yet — run Backfill weather to build the forecast."}
          </div>
          <ShortfallAlerts items={d.shortfalls || []} threshold={d.shortfall_threshold_pct} perSite={!!projectId} onSelectSite={onSelectSite} />
          <RevenueTrend monthly={d.monthly || []} short={new Set((d.shortfalls || []).map((x) => x.month))} />
        </>
      )}
    </section>
  );
}

export function OrkToolbar({ count, ranges, range, setRange, view, setView, onExport }) {
  return (
    <div className="ork-toolbar" data-testid="analytics-toolbar">
      {count != null && <span className="ork-tb-count hidden md:inline" data-testid="toolbar-site-count">Showing all {count} site{count === 1 ? "" : "s"}</span>}
      {onExport && <button className="ork-tb-btn" onClick={onExport} data-testid="toolbar-export-csv"><Download size={15} /><span className="ork-tb-lbl">CSV</span></button>}
      {count != null && <span className="ork-tb-sep hidden md:block" />}
      <select value={range} onChange={(e) => setRange(e.target.value)} className="ork-tb-select" data-testid="range-select-mobile">
        {ranges.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
      </select>
      <div className="ork-tb-range" data-testid="range-picker">
        {ranges.map((r) => (
          <button key={r.key} onClick={() => setRange(r.key)} className={range === r.key ? "is-on" : ""} data-testid={`range-${r.key}`}>{r.label}</button>
        ))}
      </div>
      {setView && (
        <div className="ork-tb-toggle" data-testid="portfolio-view-toggle">
          <button onClick={() => setView("table")} className={view === "table" ? "is-on" : ""} data-testid="portfolio-view-table"><Table2 size={15} /><span className="ork-tb-lbl">Table</span></button>
          <button onClick={() => setView("chart")} className={view === "chart" ? "is-on" : ""} data-testid="portfolio-view-chart"><BarChart3 size={15} /><span className="ork-tb-lbl">Chart</span></button>
        </div>
      )}
    </div>
  );
}

export function exportPortfolioCsv(portfolio, sites) {
  const lines = [["Site", "Generation kWh", "Potential kWh", "Variation kWh", "Variation %"].join(",")];
  sites.forEach((p) => lines.push([`"${(p.name || "").replace(/"/g, '""')}"`, p.actual_kwh ?? "", p.estimated_kwh ?? "", p.variance_kwh ?? "", p.variance_pct ?? ""].join(",")));
  lines.push("", ["Period", "Generation kWh", "Potential kWh", "Variation %"].join(","));
  (portfolio?.series || []).forEach((r) => lines.push([r.ts, r.actual_kwh ?? "", r.estimated_kwh ?? "", r.variance_pct ?? ""].join(",")));
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
  a.download = `portfolio_production_${portfolio?.range || "range"}.csv`;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 200);
}

export default function PortfolioOrkestra({ portfolio, sites, view, totalSites, rangeQuery, onSelectSite, onEdit, before }) {
  if (!portfolio) return <div className="text-sm" style={{ color: "var(--ink-2)" }}>Loading…</div>;
  const reporting = (portfolio.per_site || []).filter((r) => r.actual_kwh > 0).length;
  return (
    <>
      <KpiStrip s={portfolio.summary} reporting={reporting} total={totalSites ?? portfolio.summary.site_count} />
      <RevenuePanel rangeQuery={rangeQuery} onSelectSite={onSelectSite} />
      {before}
      {view === "table" ? <SeriesTable series={portfolio.series} /> : (
        <>
          <GenerationChart series={portfolio.series} />
          <VariationChart series={portfolio.series} />
        </>
      )}
      <SiteTable sites={sites} onSelectSite={onSelectSite} onEdit={onEdit} />
    </>
  );
}
